import React, { Fragment, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";
import { StoreFinancialYear } from "../../../api/utility/financialYearAPI";

/* -------------------- VALIDATION SCHEMA -------------------- */
const schema = yup.object().shape({
  name: yup
    .string()
    .required("Financial Year Name is required (e.g., 2025-2026)"),
  startDate: yup
    .date()
    .required("Start Date is required")
    .typeError("Invalid Date"),
  endDate: yup
    .date()
    .required("End Date is required")
    .typeError("Invalid Date")
    .min(yup.ref("startDate"), "End date must be after start date"),
  isActive: yup.boolean().default(true)
});

const AddFinancialYear = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formRef = useRef(null);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isActive: true,
      name: ""
    }
  });

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      // Note: companyId should ideally be handled by your auth middleware on the backend
      // or pulled from your global state/context here.
      const response = await StoreFinancialYear(data);

      if (response.success) {
        showToast("success", "Financial Year added successfully");
        navigate(`/utility/financial-year/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  // Custom hooks for UX
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Masters" title="Add Financial Year" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Add Financial Year</h5>
            <Link to={`/utility/financial-year/list`}>
              <button className="btn btn-primary" id="list">
                List Years
              </button>
              <UncontrolledTooltip target="list">
                View All Financial Years
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
            ref={formRef}
            className="form theme-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="card-body row">
              {/* Name */}
              <div className="col-md-4">
                <label className="form-label">Year Name (e.g. 2025-2026)</label>
                <input
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  {...register("name")}
                  placeholder="2025-2026"
                />
                {errors.name &&
                  <p className="text-danger">
                    {errors.name.message}
                  </p>}
              </div>

              {/* Start Date */}
              <div className="col-md-4">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.startDate
                    ? "is-invalid"
                    : ""}`}
                  {...register("startDate")}
                />
                {errors.startDate &&
                  <p className="text-danger">
                    {errors.startDate.message}
                  </p>}
              </div>

              {/* End Date */}
              <div className="col-md-4">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className={`form-control ${errors.endDate
                    ? "is-invalid"
                    : ""}`}
                  {...register("endDate")}
                />
                {errors.endDate &&
                  <p className="text-danger">
                    {errors.endDate.message}
                  </p>}
              </div>

              {/* Is Active */}
              <div className="col-md-4 mt-4">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isActive"
                    {...register("isActive")}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Is Active Year?
                  </label>
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button type="submit" className="btn btn-primary">
                Save Financial Year
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default AddFinancialYear;
